<template>
  <v-data-table
    v-model="selectedInternalItems"
    :disable-sort="disableSort"
    :footer-props="{ itemsPerPageOptions }"
    :headers="essentialHeadersOptions"
    :item-key="itemKey"
    :items="items"
    :items-per-page="customItemsPerPage"
    :loading="paginationLoading || loading"
    :no-data-text="noDataText"
    :options.sync="options"
    :server-items-length="maxRecords"
    :show-select="showSelect"
  >
    <template #header>
      <slot name="header" />
    </template>
    <template v-if="existSomeDefaultTopButtonActive" v-slot:top>
      <section class="d-flex align-center justify-end">
        <export-data v-if="useExportCsv" class="mx-2" :data-fetcher="exportCsv" :filename="csvFilename" ghost />
        <headers-data-table-settings
          v-if="useHeadersSettings"
          :default-headers="getDefaultHeadersOptions()"
          :headers.sync="allHeadersOptions"
          :headers-settings-name="headersSettingsName"
        />
      </section>
    </template>

    <!-- Pass on all named slots -->
    <slot v-for="slot in Object.keys($slots)" :slot="slot" :name="slot" />
    <!-- Pass on all scoped slots -->
    <template v-for="slot in Object.keys($scopedSlots)" :slot="slot" slot-scope="scope">
      <slot v-bind="scope" :name="slot" />
    </template>
  </v-data-table>
</template>

<script>
import { cloneDeep, isEqual, isEmpty } from 'lodash'
import ExportData from '@/components/ExportData.vue'
import HeadersDataTableSettings from '@/components/HeadersDataTableSettings.vue'

export default {
  components: {
    HeadersDataTableSettings,
    ExportData,
  },
  props: {
    headers: Array,
    query: Object,
    queryName: String,
    queryMainPropertyName: {
      type: String,
      required: false,
      default: '',
    },
    skip: {
      type: Boolean,
      default: false,
    },
    queryVariables: {
      type: Object,
      default: () => ({}),
    },
    search: String,
    fixedMaxRecords: Number,
    customItemsPerPage: {
      type: Number,
      default: 10,
    },
    disableSort: {
      type: Boolean,
      default: false,
    },
    page: {
      type: Number,
      default: 1,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    itemsPerPageOptions: {
      type: Array,
      default: () => [5, 10, 15],
    },
    additionalItems: {
      type: Object,
      default: () => ({}),
    },
    graphqlUri: {
      type: String,
      default: '',
    },
    arrayFunction: {
      type: String,
      default: 'map',
    },
    transformFunction: {
      type: Function,
      default: (item) => item,
    },
    showSelect: {
      type: Boolean,
      required: false,
      default: false,
    },
    itemKey: {
      type: String,
      required: false,
      default: '',
    },
    /*
      After using the 'useHeadersSettings' prop, you should use the 'headersSettingsName' prop for the localStorage to work well
    */
    useHeadersSettings: {
      type: Boolean,
      required: false,
      default: false,
    },
    useCustomHeadersSettings: {
      type: Boolean,
      required: false,
      default: false,
    },
    headersSettingsName: {
      type: String,
      required: false,
      default: '',
    },
    /*
      PLEASE READ THIS:
      1- If you are using the 'useHeadersSettings' with 'useExportCsv' is recommended don't use 'useCustomExportCsv', because the method 'exportCsv' have been create following the HeadersDataTableSettings,  so use it without 'useCustomExportCsv' prop, but that method work without HeadersDataTableSettings too. You'll need use the prop 'getCsvDataShape' with exportCsv default option, with this prop you can add specific formats for each column item, this prop is a function with a object 'shape' for table item.
      2- If you need use a custom method for exportCsv, use the 'useCustomExportCsv' with 'export-csv' $emit.
      3- If you need custom headers for csv, use 'csvHeaders' prop.
    */
    exportAllItems: {
      type: Boolean,
      default: false,
    },
    useExportCsv: {
      type: Boolean,
      required: false,
      default: false,
    },
    csvFilename: {
      type: String,
      required: false,
      default: '',
    },
    useCustomExportCsv: {
      type: Boolean,
      required: false,
      default: false,
    },
    csvHeaders: {
      type: Array,
      required: false,
      default: () => [],
    },
    csvRows: {
      type: Array,
      required: false,
      default: () => [],
    },
    getCsvDataShape: {
      type: Function,
      required: false,
      default: () => {},
    },
    noDataText: {
      type: String,
      default: 'Não há dados',
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    defaultTableSortBy: {
      type: String,
      default: '',
    },
    defaultTableSortDesc: {
      type: Boolean,
      default: false,
    },
    singleItem: {
      type: Boolean,
      default: false,
    },
    updateTable: {
      type: Boolean,
      default: false,
    },
    resetPageWhenQueryVariableChange: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    timeout: {},
    options: {},
    items: [],
    paginationLoading: false,
    maxRecords: 0,
    selectedInternalItems: [],
    allHeadersOptions: [],
  }),
  computed: {
    essentialHeadersOptions() {
      if (this.useHeadersSettings || this.useCustomHeadersSettings) {
        const currentHeaders = this.currentActiveHeadersOptions.map((header) => {
          const currentHeader = new Map(Object.entries(header))
          currentHeader.delete('isActive')
          currentHeader.delete('isDefault')
          currentHeader.delete('isRequired')

          return Object.fromEntries(currentHeader)
        })

        return currentHeaders
      } else {
        return this.headers
      }
    },
    existSomeDefaultTopButtonActive() {
      return this.useHeadersSettings || this.useExportCsv
    },
    currentActiveHeadersOptions() {
      return this.allHeadersOptions.filter((header) => header.isActive)
    },
    queryExtraArgs() {
      const extraArgs = {}
      if (this.graphqlUri) {
        extraArgs.context = {
          uri: this.graphqlUri,
        }
      }
      return extraArgs
    },
    queryPropertyName() {
      return this.queryMainPropertyName || this.queryName
    },
  },
  watch: {
    options(newValue, oldValue) {
      if (!isEmpty(oldValue)) {
        this.$emit('update-options', newValue)
        this.waitAndGetData()
      }
    },
    search(value) {
      this.options.page = 1
      this.waitAndGetData()
    },
    queryVariables(newQuery, oldQuery) {
      if (!isEqual(newQuery, oldQuery)) {
        if (this.resetPageWhenQueryVariableChange) this.options.page = 1
        this.waitAndGetData()
      }
    },
    selectedInternalItems(value) {
      if (this.showSelect) {
        this.$emit('update:selected-items', value)
      }
    },
    headers: {
      deep: true,
      handler(newValue) {
        if (this.useCustomHeadersSettings) {
          this.allHeadersOptions = newValue
        }
      },
    },
    updateTable(value) {
      if (value) {
        this.waitAndGetData()
      }
    },
  },
  mounted() {
    if (this.defaultTableSortBy) {
      this.options.sortBy = [this.defaultTableSortBy]
      this.options.sortDesc = [this.defaultTableSortDesc]
    }
    this.getData()
    this.checkIfExistAndSetSettingsOrDefaultOptions()
  },
  methods: {
    waitAndGetData() {
      clearTimeout(this.timeout)
      this.paginationLoading = true
      this.timeout = setTimeout(() => {
        this.getData()
      }, 500)
    },
    clearSelectedInternalItems() {
      this.selectedInternalItems = []
    },
    async getData() {
      if (this.skip) return
      try {
        const variables = Object.assign(
          {
            page: this.options.page || 1,
            pageSize: this.options.itemsPerPage || 10,
            search: this.search,
            sort: this.options.sortBy,
            sortDirection: this.options.sortDesc && this.options.sortDesc.map((direction) => (direction ? 'DESC' : 'ASC')),
          },
          this.queryVariables
        )

        this.paginationLoading = true
        const { data } = await this.$apollo.query({
          query: this.query,
          variables,
          fetchPolicy: 'no-cache',
          ...this.queryExtraArgs,
        })
        if (this.singleItem) {
          const dataArray = [data[this.queryName]]
          this.items = dataArray[this.arrayFunction](this.transformFunction)
        } else this.items = data[this.queryName][this.queryPropertyName][this.arrayFunction](this.transformFunction)
        this.maxRecords = this.fixedMaxRecords || data[this.queryName].records
        this.options.page = data[this.queryName].page
      } catch (error) {
        this.$snackbar({ message: 'Erro ao buscar dados no servidor', snackbarColor: 'error' })
      } finally {
        this.paginationLoading = false
        this.$emit('update:maxRecords', this.maxRecords)
        this.$emit('update:page', this.options.page)
        this.$emit('update:itemsPerPage', this.options.itemsPerPage)
      }
    },
    getDefaultHeadersOptions() {
      return cloneDeep(this.headers).map((header) => {
        header.isActive = header.isDefault
        return header
      })
    },
    checkIfExistAndSetSettingsOrDefaultOptions() {
      const settings = window.localStorage.getItem('headers-data-table-settings')
      if (settings) {
        const settingsJSON = JSON.parse(settings)
        const settingsSaved = settingsJSON?.[this.headersSettingsName]
        if (settingsSaved?.length) {
          this.allHeadersOptions = settingsSaved
        } else {
          this.allHeadersOptions = this.getDefaultHeadersOptions()
        }
      } else {
        this.allHeadersOptions = this.getDefaultHeadersOptions()
      }
    },
    getActiveValueHeadersOptions(item) {
      const dataShape = this.getCsvDataShape(item)
      const activeHeadersValue = this.currentActiveHeadersOptions.reduce(
        (allHeaders, currentHeader) => ({
          ...allHeaders,
          ...{
            [currentHeader.value]: dataShape[currentHeader.value],
          },
        }),
        {}
      )

      return activeHeadersValue
    },
    async exportCsv() {
      try {
        const variables = Object.assign(
          {
            page: this.options.page || 1,
            pageSize: this.options.itemsPerPage || 10,
            search: this.search,
            sort: this.options.sortBy,
            sortDirection: this.options.sortDesc && this.options.sortDesc.map((direction) => (direction ? 'DESC' : 'ASC')),
          },
          this.queryVariables
        )
        const { data } = await this.$apollo.query({
          query: this.query,
          variables: variables,
          fetchPolicy: 'no-cache',
          ...this.queryExtraArgs,
        })
        const headers = this.csvHeaders.length ? this.csvHeaders : this.currentActiveHeadersOptions.map(({ text }) => text)
        const rowsValues = this.csvRows.length ? this.csvRows : this.currentActiveHeadersOptions.map(({ value }) => value)
        const rows = data?.[this.queryName]?.[this.queryPropertyName].map((item) => {
          const shape = this.getCsvDataShape(item)
          let row = []
          rowsValues.map((value) => {
            row.push(shape ? shape[value] : item[value])
          })
          return row
        })
        return { headers, rows }
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Falha ao tentar exportar como csv' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}
</style>
